// Storage keys for different types of data
export const STORAGE_KEYS = {
   FORM_DATA: "driver_verification_form_data",
   FILES_INFO: "driver_verification_files_info",
   LOCATION_DATA: "driver_verification_location_data",
};

class LocalStorageUtils {
   /**
    * Add/Set an item in localStorage
    * @param {string} key - The key to store the value under
    * @param {any} value - The value to store (will be JSON stringified)
    * @returns {boolean} - True if successful, false otherwise
    */
   static add(key, value) {
      try {
         if (typeof key !== "string") {
            console.warn("LocalStorageUtils: Key must be a string");
            return false;
         }

         const serializedValue = JSON.stringify(value);
         localStorage.setItem(key, serializedValue);
         return true;
      } catch (error) {
         console.error("LocalStorageUtils: Error adding item:", error);
         return false;
      }
   }

   /**
    * Update an existing item in localStorage
    * @param {string} key - The key to update
    * @param {any} value - The new value to store (will be JSON stringified)
    * @returns {boolean} - True if successful, false otherwise
    */
   static update(key, value) {
      try {
         if (typeof key !== "string") {
            console.warn("LocalStorageUtils: Key must be a string");
            return false;
         }

         // Check if the key exists before updating
         if (!this.check(key)) {
            console.warn("LocalStorageUtils: Key does not exist, cannot update");
            return false;
         }

         const serializedValue = JSON.stringify(value);
         localStorage.setItem(key, serializedValue);
         return true;
      } catch (error) {
         console.error("LocalStorageUtils: Error updating item:", error);
         return false;
      }
   }

   /**
    * Delete/Remove an item from localStorage
    * @param {string} key - The key to remove
    * @returns {boolean} - True if successful, false otherwise
    */
   static delete(key) {
      try {
         if (typeof key !== "string") {
            console.warn("LocalStorageUtils: Key must be a string");
            return false;
         }

         localStorage.removeItem(key);
         return true;
      } catch (error) {
         console.error("LocalStorageUtils: Error deleting item:", error);
         return false;
      }
   }

   /**
    * Clear all items from localStorage
    * @returns {boolean} - True if successful, false otherwise
    */
   static clear() {
      try {
         localStorage.clear();
         return true;
      } catch (error) {
         console.error("LocalStorageUtils: Error clearing localStorage:", error);
         return false;
      }
   }

   /**
    * Check if a key exists in localStorage
    * @param {string} key - The key to check
    * @returns {boolean} - True if key exists, false otherwise
    */
   static check(key) {
      try {
         if (typeof key !== "string") {
            return false;
         }
         return localStorage.getItem(key) !== null;
      } catch (error) {
         console.error("LocalStorageUtils: Error checking item existence:", error);
         return false;
      }
   }

   /**
    * Check if files exist in localStorage
    * @returns {boolean} True if files exist, false otherwise
    */
   static hasSavedFiles() {
      return this.check(STORAGE_KEYS.FILES_INFO);
   }

   /**
    * Convert a File object to base64 data URL
    * @param {File} file - The file to convert
    * @returns {Promise<string>} Base64 data URL
    */
   static async fileToBase64(file) {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.readAsDataURL(file);
         reader.onload = () => resolve(reader.result);
         reader.onerror = (error) => reject(error);
      });
   }

   /**
    * Convert base64 data URL back to File object
    * @param {string} base64String - The base64 data URL
    * @param {string} fileName - The original file name
    * @param {string} mimeType - The file MIME type
    * @returns {Promise<File>} File object
    */
   static async base64ToFile(base64String, fileName, mimeType) {
      const response = await fetch(base64String);
      const blob = await response.blob();
      return new File([blob], fileName, { type: mimeType });
   }

   /**
    * Save files to localStorage as base64 strings
    * @param {Object} files - Object containing file references
    * @returns {Promise<Object>} Saved files data
    */
   static async saveFilesToStorage(files) {
      try {
         const filesData = {};

         for (const [key, file] of Object.entries(files)) {
            if (file) {
               try {
                  const base64 = await this.fileToBase64(file);
                  filesData[key] = {
                     name: file.name,
                     type: file.type,
                     size: file.size,
                     data: base64,
                  };
               } catch (error) {
                  console.error(`Error converting file ${key} to base64:`, error);
               }
            }
         }

         this.add(STORAGE_KEYS.FILES_INFO, filesData);

         return filesData;
      } catch (error) {
         console.error("Error saving files to localStorage:", error);
         throw error;
      }
   }

   /**
    * Load files from localStorage and convert back to File objects
    * @returns {Promise<Object>} Restored files object
    */
   static async loadFilesFromStorage() {
      try {
         const savedFilesInfo = this.check(STORAGE_KEYS.FILES_INFO)
            ? JSON.parse(localStorage.getItem(STORAGE_KEYS.FILES_INFO))
            : null;

         if (!savedFilesInfo) {
            return {};
         }

         const restoredFiles = {};

         for (const [key, fileInfo] of Object.entries(savedFilesInfo)) {
            if (fileInfo && fileInfo.data) {
               try {
                  const file = await this.base64ToFile(fileInfo.data, fileInfo.name, fileInfo.type);
                  restoredFiles[key] = file;
               } catch (error) {
                  console.error(`Error restoring file ${key}:`, error);
               }
            }
         }

         return restoredFiles;
      } catch (error) {
         console.error("Error loading files from localStorage:", error);
         return {};
      }
   }

   /**
    * Clear all form-related data from localStorage
    * @returns {boolean} - True if successful, false otherwise
    */
   static clearFormData() {
      try {
         this.delete(STORAGE_KEYS.FORM_DATA);
         this.delete(STORAGE_KEYS.FILES_INFO);
         this.delete(STORAGE_KEYS.LOCATION_DATA);
         return true;
      } catch (error) {
         console.error("LocalStorageUtils: Error clearing form data:", error);
         return false;
      }
   }

   /**
    * Load all form-related data from localStorage
    * @returns {Object} Object containing form data, files info, and location data
    */
   static loadFormData() {
      try {
         const savedFormData = this.check(STORAGE_KEYS.FORM_DATA)
            ? JSON.parse(localStorage.getItem(STORAGE_KEYS.FORM_DATA))
            : null;

         const savedFilesInfo = this.check(STORAGE_KEYS.FILES_INFO)
            ? JSON.parse(localStorage.getItem(STORAGE_KEYS.FILES_INFO))
            : null;

         const savedLocationData = this.check(STORAGE_KEYS.LOCATION_DATA)
            ? JSON.parse(localStorage.getItem(STORAGE_KEYS.LOCATION_DATA))
            : null;

         return {
            formData: savedFormData,
            filesInfo: savedFilesInfo,
            locationData: savedLocationData,
         };
      } catch (error) {
         console.error("LocalStorageUtils: Error loading form data:", error);
         return {
            formData: null,
            filesInfo: null,
            locationData: null,
         };
      }
   }
}

export default LocalStorageUtils;
